import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { supabase } from '../../../lib/supabaseClient';

const SystemStatisticsPanel = () => {
  const [timeRange, setTimeRange] = useState('7days');
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [counts, setCounts] = useState({ users: 0, projects: 0, challenges: 0, proofs: 0 });
  const [analyticsTab, setAnalyticsTab] = useState('realtime');

  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState('');

  const [realtimeSummary, setRealtimeSummary] = useState({ active_sessions: 0, active_users: 0 });
  const [realtimeKpis, setRealtimeKpis] = useState({
    active_sessions: 0,
    active_users: 0,
    pageviews: 0,
    avg_pageviews_per_session: 0,
    avg_engagement_seconds: 0,
  });
  const [realtimeChannels, setRealtimeChannels] = useState([]);
  const [realtimePages, setRealtimePages] = useState([]);

  const [acquisitionChannels, setAcquisitionChannels] = useState([]);
  const [acquisitionCampaigns, setAcquisitionCampaigns] = useState([]);
  const [acquisitionKpis, setAcquisitionKpis] = useState({ sessions: 0, new_sessions: 0, returning_sessions: 0 });

  const [engagementSummary, setEngagementSummary] = useState({ avg_engagement_seconds: 0 });
  const [engagementTopPages, setEngagementTopPages] = useState([]);
  const [engagementKpis, setEngagementKpis] = useState({ avg_engagement_seconds: 0, bounce_rate: 0 });

  const [techDevices, setTechDevices] = useState([]);
  const [demographicLocales, setDemographicLocales] = useState([]);
  const [demographicTimezones, setDemographicTimezones] = useState([]);

  const timeRangeOptions = [
    { value: '7days', label: '최근 7일' },
    { value: '30days', label: '최근 30일' },
    { value: '90days', label: '최근 90일' },
    { value: 'year', label: '올해' },
  ];

  const platformStats = [
    { label: '총 사용자', value: String(counts.users), icon: 'Users', color: 'var(--color-primary)' },
    { label: '프로젝트', value: String(counts.projects), icon: 'LayoutGrid', color: 'var(--color-success)' },
    { label: '챌린지', value: String(counts.challenges), icon: 'Target', color: 'var(--color-accent)' },
    { label: '증명', value: String(counts.proofs), icon: 'CheckCircle', color: 'var(--color-warning)' },
  ];

  const analyticsTabs = [
    { id: 'realtime', label: '실시간 보고서', icon: 'Radar' },
    { id: 'acquisition', label: '유입 경로', icon: 'Share2' },
    { id: 'engagement', label: '참여도', icon: 'Activity' },
    { id: 'demographics', label: '인구통계/기술', icon: 'Globe' },
  ];

  const userGrowthData = [];
  const activityData = [];
  const challengeTypeData = [];
  const engagementData = [];

  const daysFromTimeRange = (value) => {
    if (value === '30days') return 30;
    if (value === '90days') return 90;
    if (value === 'year') return 365;
    return 7;
  };

  const formatSeconds = (secondsValue) => {
    const total = Math.max(0, Number(secondsValue) || 0);
    const m = Math.floor(total / 60);
    const s = total % 60;
    if (m <= 0) return `${s}초`;
    return `${m}분 ${String(s).padStart(2, '0')}초`;
  };

  const labelChannel = (id) => {
    if (id === 'direct') return '직접 접속';
    if (id === 'search') return '검색';
    if (id === 'social') return '소셜';
    if (id === 'referral') return '리퍼러';
    if (id === 'campaign') return '캠페인';
    return id || '기타';
  };

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setLoadError('');
      try {
        const [usersRes, projectsRes, challengesRes, proofsRes] = await Promise.all([
          supabase.from('ch_profiles').select('id', { count: 'exact', head: true }),
          supabase.from('ch_projects').select('id', { count: 'exact', head: true }),
          supabase.from('ch_challenges').select('id', { count: 'exact', head: true }),
          supabase.from('ch_proofs').select('id', { count: 'exact', head: true }),
        ]);

        const anyError = usersRes.error || projectsRes.error || challengesRes.error || proofsRes.error;
        if (anyError) {
          setCounts({ users: 0, projects: 0, challenges: 0, proofs: 0 });
          setLoadError(anyError?.message || '통계를 불러오지 못했습니다.');
          return;
        }

        setCounts({
          users: usersRes.count || 0,
          projects: projectsRes.count || 0,
          challenges: challengesRes.count || 0,
          proofs: proofsRes.count || 0,
        });
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [timeRange]);

  useEffect(() => {
    const loadAnalytics = async () => {
      setAnalyticsLoading(true);
      setAnalyticsError('');

      const days = daysFromTimeRange(timeRange);

      try {
        if (analyticsTab === 'realtime') {
          const [summaryRes, channelsRes, pagesRes] = await Promise.all([
            supabase.rpc('ch_analytics_realtime', { p_window_minutes: 30 }),
            supabase.rpc('ch_analytics_realtime_channels', { p_window_minutes: 30 }),
            supabase.rpc('ch_analytics_realtime_top_pages', { p_window_minutes: 30, p_limit: 10 }),
          ]);

          const { data: realtimeKpisData, error: realtimeKpisErr } = await supabase
            .rpc('ch_analytics_realtime_kpis', { p_window_minutes: 30 });

          const anyErr = summaryRes.error || channelsRes.error || pagesRes.error || realtimeKpisErr;
          if (anyErr) {
            setAnalyticsError(anyErr?.message || '분석 데이터를 불러오지 못했습니다.');
            return;
          }

          setRealtimeSummary(summaryRes.data?.[0] || { active_sessions: 0, active_users: 0 });
          setRealtimeKpis(realtimeKpisData?.[0] || {
            active_sessions: 0,
            active_users: 0,
            pageviews: 0,
            avg_pageviews_per_session: 0,
            avg_engagement_seconds: 0,
          });
          setRealtimeChannels(channelsRes.data || []);
          setRealtimePages(pagesRes.data || []);
          return;
        }

        if (analyticsTab === 'acquisition') {
          const [channelsRes, campaignsRes] = await Promise.all([
            supabase.rpc('ch_analytics_acquisition_channels', { p_days: days }),
            supabase.rpc('ch_analytics_acquisition_campaigns', { p_days: days, p_limit: 10 }),
          ]);

          const { data: acquisitionKpisData, error: acquisitionKpisErr } = await supabase
            .rpc('ch_analytics_acquisition_kpis', { p_days: days });

          const anyErr = channelsRes.error || campaignsRes.error || acquisitionKpisErr;
          if (anyErr) {
            setAnalyticsError(anyErr?.message || '분석 데이터를 불러오지 못했습니다.');
            return;
          }

          setAcquisitionChannels(channelsRes.data || []);
          setAcquisitionCampaigns(campaignsRes.data || []);
          setAcquisitionKpis(acquisitionKpisData?.[0] || { sessions: 0, new_sessions: 0, returning_sessions: 0 });
          return;
        }

        if (analyticsTab === 'engagement') {
          const [summaryRes, topPagesRes] = await Promise.all([
            supabase.rpc('ch_analytics_engagement_summary', { p_days: days }),
            supabase.rpc('ch_analytics_engagement_top_pages', { p_days: days, p_limit: 10 }),
          ]);

          const { data: engagementKpisData, error: engagementKpisErr } = await supabase
            .rpc('ch_analytics_engagement_kpis', { p_days: days });

          const anyErr = summaryRes.error || topPagesRes.error || engagementKpisErr;
          if (anyErr) {
            setAnalyticsError(anyErr?.message || '분석 데이터를 불러오지 못했습니다.');
            return;
          }

          setEngagementSummary(summaryRes.data?.[0] || { avg_engagement_seconds: 0 });
          setEngagementTopPages(topPagesRes.data || []);
          setEngagementKpis(engagementKpisData?.[0] || { avg_engagement_seconds: 0, bounce_rate: 0 });
          return;
        }

        if (analyticsTab === 'demographics') {
          const [devicesRes, localesRes, tzRes] = await Promise.all([
            supabase.rpc('ch_analytics_tech_devices', { p_days: days }),
            supabase.rpc('ch_analytics_demographics_locales', { p_days: days, p_limit: 10 }),
            supabase.rpc('ch_analytics_demographics_timezones', { p_days: days, p_limit: 10 }),
          ]);

          const anyErr = devicesRes.error || localesRes.error || tzRes.error;
          if (anyErr) {
            setAnalyticsError(anyErr?.message || '분석 데이터를 불러오지 못했습니다.');
            return;
          }

          setTechDevices(devicesRes.data || []);
          setDemographicLocales(localesRes.data || []);
          setDemographicTimezones(tzRes.data || []);
        }
      } finally {
        setAnalyticsLoading(false);
      }
    };

    loadAnalytics();
  }, [analyticsTab, timeRange]);

  const handleExport = () => {
    console.log('Exporting report...');
  };

  return (
    <div className="space-y-6 md:space-y-8">
      {loadError ? (
        <div className="bg-card rounded-lg shadow-gaming-md p-6">
          <p className="text-sm text-muted-foreground caption">{loadError}</p>
        </div>
      ) : null}
      {isLoading ? (
        <div className="bg-card rounded-lg shadow-gaming-md p-6">
          <p className="text-sm text-muted-foreground caption">로딩 중...</p>
        </div>
      ) : null}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/20 rounded-md flex items-center justify-center">
            <Icon name="BarChart3" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold text-foreground">시스템 통계</h3>
            <p className="text-sm text-muted-foreground caption">플랫폼 전체 성과 지표</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Select
            options={timeRangeOptions}
            value={timeRange}
            onChange={setTimeRange}
            placeholder="기간 선택"
          />
          <Button variant="outline" iconName="Download" onClick={handleExport}>
            리포트 내보내기
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {platformStats?.map((stat, index) => (
          <div
            key={index}
            className="bg-card rounded-lg shadow-gaming-sm p-4 md:p-6 space-y-3"
          >
            <div className="flex items-center justify-between">
              <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ backgroundColor: `${stat?.color}20` }}>
                <Icon name={stat?.icon} size={20} color={stat?.color} />
              </div>
            </div>
            <div>
              <div className="text-2xl md:text-3xl font-heading font-bold text-foreground data-text">
                {stat?.value}
              </div>
              <div className="text-sm text-muted-foreground caption mt-1">{stat?.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
          <div>
            <h4 className="text-base font-heading font-semibold text-foreground">핵심 분석 탭</h4>
            <p className="text-sm text-muted-foreground caption">서비스 내 이벤트 로그를 기반으로 실시간/유입/참여/기기 정보를 제공합니다.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {analyticsTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setAnalyticsTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-gaming ${
                  analyticsTab === tab.id
                    ? 'bg-primary text-primary-foreground shadow-gaming-sm'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                <Icon name={tab.icon} size={16} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {analyticsError ? (
          <div className="mb-4 rounded-md border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground caption">
            {analyticsError}
          </div>
        ) : null}

        {analyticsLoading ? (
          <div className="mb-4 rounded-md border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground caption">
            분석 데이터 로딩 중...
          </div>
        ) : null}

        {analyticsTab === 'realtime' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-muted/40 rounded-lg p-4 border border-border">
              <div className="text-sm text-muted-foreground caption mb-2">지난 30분 활성 사용자</div>
              <div className="text-3xl font-heading font-bold text-foreground data-text">{realtimeKpis.active_users || 0}명</div>
              <div className="text-xs text-muted-foreground mt-1">활성 세션 {realtimeKpis.active_sessions || 0}개</div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <div className="rounded-md border border-border bg-card px-3 py-2">
                  <div className="text-[11px] text-muted-foreground caption">페이지뷰</div>
                  <div className="text-sm font-semibold text-foreground">{realtimeKpis.pageviews || 0}</div>
                </div>
                <div className="rounded-md border border-border bg-card px-3 py-2">
                  <div className="text-[11px] text-muted-foreground caption">페이지/세션</div>
                  <div className="text-sm font-semibold text-foreground">{Number(realtimeKpis.avg_pageviews_per_session || 0).toFixed(2)}</div>
                </div>
              </div>
            </div>
            <div className="bg-muted/40 rounded-lg p-4 border border-border">
              <div className="text-sm font-semibold text-foreground mb-2">실시간 유입 채널</div>
              <div className="space-y-2 text-sm text-muted-foreground">
                {(realtimeChannels || []).length === 0 ? (
                  <div className="text-xs text-muted-foreground caption">데이터 없음</div>
                ) : (
                  (realtimeChannels || []).slice(0, 6).map((c) => (
                    <div key={c.channel} className="flex items-center justify-between">
                      <span>{labelChannel(c.channel)}</span>
                      <span className="text-foreground font-medium">{c.sessions}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="bg-muted/40 rounded-lg p-4 border border-border">
              <div className="text-sm font-semibold text-foreground mb-2">현재 보고 있는 페이지</div>
              <div className="space-y-2 text-sm text-muted-foreground">
                {(realtimePages || []).length === 0 ? (
                  <div className="text-xs text-muted-foreground caption">데이터 없음</div>
                ) : (
                  (realtimePages || []).slice(0, 6).map((p) => (
                    <div key={`${p.path}:${p.sessions}`} className="flex items-center justify-between">
                      <span className="truncate max-w-[60%]">{p.path || '(unknown)'}</span>
                      <span className="text-foreground font-medium">{p.sessions}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {analyticsTab === 'acquisition' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-muted/40 rounded-lg p-4 border border-border">
              <div className="text-sm font-semibold text-foreground mb-2">유입 채널 분포</div>
              <div className="space-y-2 text-sm text-muted-foreground">
                {(acquisitionChannels || []).length === 0 ? (
                  <div className="text-xs text-muted-foreground caption">데이터 없음</div>
                ) : (
                  (acquisitionChannels || []).slice(0, 6).map((c) => (
                    <div key={c.channel} className="flex items-center justify-between">
                      <span>{labelChannel(c.channel)}</span>
                      <span className="text-foreground font-medium">{c.sessions}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="bg-muted/40 rounded-lg p-4 border border-border">
              <div className="text-sm font-semibold text-foreground mb-2">상위 캠페인</div>
              <div className="space-y-2 text-sm text-muted-foreground">
                {(acquisitionCampaigns || []).length === 0 ? (
                  <div className="text-xs text-muted-foreground caption">데이터 없음</div>
                ) : (
                  (acquisitionCampaigns || []).slice(0, 6).map((c) => (
                    <div key={c.campaign} className="flex items-center justify-between">
                      <span className="truncate max-w-[60%]">{c.campaign}</span>
                      <span className="text-foreground font-medium">{c.sessions}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="bg-muted/40 rounded-lg p-4 border border-border">
              <div className="text-sm font-semibold text-foreground mb-2">유입 품질</div>
              <div className="space-y-3 text-sm text-muted-foreground">
                <div className="flex items-center justify-between">
                  <span>세션</span>
                  <span className="text-foreground font-medium">{acquisitionKpis.sessions || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>신규 세션</span>
                  <span className="text-foreground font-medium">{acquisitionKpis.new_sessions || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>재방문 세션</span>
                  <span className="text-foreground font-medium">{acquisitionKpis.returning_sessions || 0}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {analyticsTab === 'engagement' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-muted/40 rounded-lg p-4 border border-border">
              <div className="text-sm text-muted-foreground caption mb-2">평균 참여 시간</div>
              <div className="text-3xl font-heading font-bold text-foreground data-text">
                {formatSeconds(engagementKpis.avg_engagement_seconds || engagementSummary.avg_engagement_seconds || 0)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">page_engagement 이벤트 기반 평균</div>
            </div>
            <div className="bg-muted/40 rounded-lg p-4 border border-border">
              <div className="text-sm font-semibold text-foreground mb-2">상위 콘텐츠</div>
              <div className="space-y-2 text-sm text-muted-foreground">
                {(engagementTopPages || []).length === 0 ? (
                  <div className="text-xs text-muted-foreground caption">데이터 없음</div>
                ) : (
                  (engagementTopPages || []).slice(0, 6).map((p) => (
                    <div key={p.path} className="flex items-center justify-between">
                      <span className="truncate max-w-[60%]">{p.path || '(unknown)'}</span>
                      <span className="text-foreground font-medium">{formatSeconds(p.avg_engagement_seconds || 0)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="bg-muted/40 rounded-lg p-4 border border-border">
              <div className="text-sm font-semibold text-foreground mb-2">참여 지표</div>
              <div className="space-y-3 text-sm text-muted-foreground">
                <div className="flex items-center justify-between">
                  <span>이탈률(세션당 1페이지)</span>
                  <span className="text-foreground font-medium">{`${Math.round(Number(engagementKpis.bounce_rate || 0) * 100)}%`}</span>
                </div>
                <div className="text-xs text-muted-foreground caption">이탈률은 page_view 기준 세션당 페이지뷰 1회로 계산합니다.</div>
              </div>
            </div>
          </div>
        )}

        {analyticsTab === 'demographics' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-muted/40 rounded-lg p-4 border border-border">
              <div className="text-sm font-semibold text-foreground mb-2">로케일(언어/지역)</div>
              <div className="space-y-2 text-sm text-muted-foreground">
                {(demographicLocales || []).length === 0 ? (
                  <div className="text-xs text-muted-foreground caption">데이터 없음</div>
                ) : (
                  (demographicLocales || []).slice(0, 6).map((r) => (
                    <div key={r.locale} className="flex items-center justify-between">
                      <span className="truncate max-w-[60%]">{r.locale}</span>
                      <span className="text-foreground font-medium">{r.sessions}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="bg-muted/40 rounded-lg p-4 border border-border">
              <div className="text-sm font-semibold text-foreground mb-2">타임존</div>
              <div className="space-y-2 text-sm text-muted-foreground">
                {(demographicTimezones || []).length === 0 ? (
                  <div className="text-xs text-muted-foreground caption">데이터 없음</div>
                ) : (
                  (demographicTimezones || []).slice(0, 6).map((r) => (
                    <div key={r.timezone} className="flex items-center justify-between">
                      <span className="truncate max-w-[60%]">{r.timezone}</span>
                      <span className="text-foreground font-medium">{r.sessions}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="bg-muted/40 rounded-lg p-4 border border-border">
              <div className="text-sm font-semibold text-foreground mb-2">사용 기기</div>
              <div className="space-y-2 text-sm text-muted-foreground">
                {(techDevices || []).length === 0 ? (
                  <div className="text-xs text-muted-foreground caption">데이터 없음</div>
                ) : (
                  (techDevices || []).slice(0, 6).map((r) => (
                    <div key={r.device_type} className="flex items-center justify-between">
                      <span>{r.device_type}</span>
                      <span className="text-foreground font-medium">{r.sessions}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6">
          <h4 className="text-base font-heading font-semibold text-foreground mb-4">사용자 증가 추이</h4>
          <div className="w-full h-64 flex items-center justify-center" aria-label="사용자 증가 추이 라인 차트">
            <p className="text-sm text-muted-foreground caption">차트 데이터 준비중</p>
          </div>
        </div>

        <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6">
          <h4 className="text-base font-heading font-semibold text-foreground mb-4">챌린지 유형 분포</h4>
          <div className="w-full h-64 flex items-center justify-center" aria-label="챌린지 유형 분포 파이 차트">
            <p className="text-sm text-muted-foreground caption">차트 데이터 준비중</p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6">
          <h4 className="text-base font-heading font-semibold text-foreground mb-4">활동 통계</h4>
          <div className="w-full h-64 flex items-center justify-center" aria-label="활동 통계 바 차트">
            <p className="text-sm text-muted-foreground caption">차트 데이터 준비중</p>
          </div>
        </div>

        <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6">
          <h4 className="text-base font-heading font-semibold text-foreground mb-4">주간 참여도</h4>
          <div className="w-full h-64 flex items-center justify-center" aria-label="주간 참여도 바 차트">
            <p className="text-sm text-muted-foreground caption">차트 데이터 준비중</p>
          </div>
        </div>
      </div>
      <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6">
        <h4 className="text-base font-heading font-semibold text-foreground mb-4">주요 지표 요약</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground caption">평균 세션 시간</span>
              <Icon name="Clock" size={16} className="text-muted-foreground" />
            </div>
            <div className="text-xl font-heading font-bold text-foreground data-text">24분 32초</div>
          </div>
          <div className="p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground caption">일일 활성 사용자</span>
              <Icon name="Activity" size={16} className="text-muted-foreground" />
            </div>
            <div className="text-xl font-heading font-bold text-foreground data-text">8,945명</div>
          </div>
          <div className="p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground caption">평균 완료율</span>
              <Icon name="Target" size={16} className="text-muted-foreground" />
            </div>
            <div className="text-xl font-heading font-bold text-foreground data-text">78.4%</div>
          </div>
          <div className="p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground caption">신규 가입자</span>
              <Icon name="UserPlus" size={16} className="text-muted-foreground" />
            </div>
            <div className="text-xl font-heading font-bold text-foreground data-text">342명</div>
          </div>
          <div className="p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground caption">이탈률</span>
              <Icon name="TrendingDown" size={16} className="text-muted-foreground" />
            </div>
            <div className="text-xl font-heading font-bold text-foreground data-text">12.3%</div>
          </div>
          <div className="p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground caption">재방문율</span>
              <Icon name="Repeat" size={16} className="text-muted-foreground" />
            </div>
            <div className="text-xl font-heading font-bold text-foreground data-text">65.7%</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemStatisticsPanel;